/**
 * Property access shared by every module that reads an untrusted error object
 * (SDK errors, ChargeBee payloads, values thrown across a realm boundary).
 * An accessor on such an object may throw rather than answer, so every read
 * goes through here instead of a bare `value[key]`.
 */

export function asRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : null;
}

export function readProperty(source: object, key: string): unknown {
    try {
        return (source as Record<string, unknown>)[key];
    } catch {
        return undefined;
    }
}

/**
 * A vendor id/token cannot be allow-listed by value, so it is constrained by
 * shape instead: short, and made only of the characters a request id or
 * intent id is ever shaped from. Used for fields that are read from the same
 * object as free-form vendor prose, which must never land in a telemetry
 * field.
 */
const OPAQUE_TOKEN_SHAPE = /^[A-Za-z0-9_-]+$/;

export function asOpaqueToken(
    value: unknown,
    maxLength: number
): string | undefined {
    if (typeof value !== "string") return undefined;

    const token = value.trim();

    return token.length > 0 &&
        token.length <= maxLength &&
        OPAQUE_TOKEN_SHAPE.test(token)
        ? token
        : undefined;
}
